import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Check, ArrowLeft } from "lucide-react";
import Seo from "@/components/site/Seo";
import { trackCall, trackEvent, trackLeadConversion } from "@/lib/analytics";
import { CONTACT } from "@/data/content";

// "Request received" confirmation page.
// Every successful quote/enquiry submission lands here, so a page_view on
// /thank-you is a confirmed lead — this is the URL to give Google
// (Business Profile "request a quote" + Ads page-load conversions).

export default function ThankYou() {
  useEffect(() => {
    window.scrollTo(0, 0);
    // Explicit virtual page_view so SPA navigations register in GA4 —
    // this URL is the conversion signal for Google.
    trackEvent("page_view", {
      page_location: window.location.href,
      page_title: "Request received | Perth Tiling WA",
    });
    trackEvent("lead_confirmed", { event_category: "engagement" });
    trackLeadConversion();
  }, []);

  return (
    <>
      <Seo pageKey="home" overrideTitle="Request received | Perth Tiling WA" overrideDescription="Thank you — your enquiry has been received by Perth Tiling WA. We'll be in touch shortly." noindex />
      <div data-testid="thank-you-page" className="bg-[#f8fafc] min-h-screen flex flex-col">
        <header className="bg-[#0b1120] py-4">
          <div className="mx-auto max-w-[1200px] px-6 flex items-center justify-between">
            <Link to="/">
              <img src="/brand/logo.png" alt="Perth Tiling WA" className="h-10 md:h-12 w-auto object-contain" />
            </Link>
            <a
              href={`tel:${CONTACT.phoneRaw}`}
              onClick={() => trackCall("thank_you_header")}
              data-testid="thank-you-call"
              className="inline-flex items-center gap-2 bg-[#9c7a4d] text-white hover:bg-white hover:text-[#0f172a] px-5 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors"
            >
              <Phone className="h-3.5 w-3.5" /> {CONTACT.phone}
            </a>
          </div>
        </header>

        <main className="flex-1 py-16 md:py-24">
          <div className="mx-auto max-w-[1200px] px-6">
            <p className="overline !text-[#9c7a4d] mb-5 flex items-center gap-2">
              <Check className="h-4 w-4" /> Request received
            </p>
            <h1 className="font-serif font-light text-[#0f172a] text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight max-w-3xl">
              Thank you.
            </h1>
            <p className="mt-6 max-w-xl text-[#475569] text-lg md:text-xl font-light leading-relaxed">
              Your enquiry has landed with our team. We'll be in touch shortly — for anything urgent, call us on{" "}
              <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCall("thank_you_body")} className="text-[#9c7a4d]">{CONTACT.phone}</a>.
            </p>

            <div className="mt-14">
              <Link to="/" data-testid="thank-you-home-link" className="inline-flex items-center gap-2 text-[#64748b] hover:text-[#9c7a4d] text-sm uppercase tracking-[0.2em] transition-colors">
                <ArrowLeft className="h-4 w-4" /> Back to home
              </Link>
            </div>
          </div>
        </main>

        <footer className="bg-[#0b1120] text-[#94a3b8] py-8">
          <div className="mx-auto max-w-[1200px] px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
            <p>© {new Date().getFullYear()} Perth Tiling WA. All rights reserved.</p>
            <a href={`mailto:${CONTACT.email}`} className="hover:text-[#c9a87c] transition-colors">{CONTACT.email}</a>
          </div>
        </footer>
      </div>
    </>
  );
}
